/**
 * ==========================================================================
 *  API-Wrapper für das Pflege-Assistent-Cloud-Backend
 * ==========================================================================
 *  Warum ein eigener Wrapper?
 *   - 'credentials: include' ist Pflicht, damit der Browser den
 *     Session-Cookie automatisch mitschickt. Vergisst man das, sieht das
 *     Backend jeden Request als "nicht eingeloggt".
 *   - Gemeinsame Fehlerbehandlung (z.B. 401 → Login-Redirect).
 *   - Base-URL zentral. In Dev zeigt der Vite-Proxy /api auf localhost:8000,
 *     in Prod zeigt Nginx /api auf den Backend-Port — in beiden Fällen
 *     funktionieren relative URLs ("/api/...").
 *
 *  Typische Aufrufe:
 *    const me = await api.get('/api/auth/me');
 *    const r  = await api.post('/api/auth/login', { email, passwort });
 * ==========================================================================
 */

const BASE = '';  // relativ — Vite-Proxy (dev) / Nginx (prod) macht den Rest

/**
 * Fehlerklasse für HTTP-Antworten, die kein 2xx sind.
 * Enthält status und die vom Backend gesendete 'detail'-Meldung.
 */
export class ApiError extends Error {
  constructor(status, detail, body) {
    super(detail || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

/**
 * Interner Helfer. Macht den eigentlichen fetch() und packt Fehler aus.
 */
async function request(method, path, bodyObject) {
  const options = {
    method,
    credentials: 'include',  // Session-Cookies MIT jedem Request
    headers: { 'Content-Type': 'application/json' },
  };
  if (bodyObject !== undefined) {
    options.body = JSON.stringify(bodyObject);
  }

  const response = await fetch(BASE + path, options);

  // Kein Content (z.B. 204) → nichts zurückgeben
  if (response.status === 204) return null;

  // JSON parsen (kann bei Fehlern auch HTML-Seiten zurückkommen)
  let data = null;
  try {
    data = await response.json();
  } catch {
    // Antwort war kein JSON — egal, wir werfen eh den Status
  }

  if (!response.ok) {
    throw new ApiError(response.status, data?.detail, data);
  }
  return data;
}

/**
 * Wie request(), aber für multipart/form-data (Datei-Uploads). Wichtig:
 * KEIN Content-Type-Header setzen — der Browser ergänzt automatisch die
 * multipart-Boundary. FormData wird direkt als Body übergeben.
 */
async function requestForm(method, path, formData) {
  const response = await fetch(BASE + path, {
    method,
    credentials: 'include',
    body: formData,
  });
  if (response.status === 204) return null;
  let data = null;
  try { data = await response.json(); } catch { /* kein JSON */ }
  if (!response.ok) {
    throw new ApiError(response.status, data?.detail, data);
  }
  return data;
}

export const api = {
  get:  (path) => request('GET',  path),
  post: (path, body) => request('POST', path, body),
  put:  (path, body) => request('PUT',  path, body),
  del:  (path) => request('DELETE', path),
  postForm: (path, formData) => requestForm('POST', path, formData),
};
